/**
 * Optional dashboard sections an admin can switch off from /settingsdb.
 * Keys match the backend's SettingController::FEATURE_TOGGLES. Switching one
 * off only hides it in the UI; data and commissions keep working underneath.
 */
export const FEATURE_INCENTIVES = 'incentives'
export const FEATURE_ANNOUNCEMENTS = 'announcements'
export const FEATURE_LOCATIONS = 'locations'
export const FEATURE_VIP = 'vip'
export const FEATURE_NEW_MEMBERS = 'new_members'
export const FEATURE_FUNDS_NAV = 'funds_nav'
// Unlike the rest, these two are also ENFORCED by the backend
// (App\Services\WithdrawalSwitches). They pause the USDT (crypto) rails only:
// off drops the crypto options and the server refuses crypto requests, while
// Bank/Ewallet keeps following the cashout switch. Use useWithdrawalRails().
export const FEATURE_PAYOUT_WITHDRAWALS = 'payout_withdrawals'
export const FEATURE_COMMISSION_WITHDRAWALS = 'commission_withdrawals'

// Not a Features-card toggle: this is the "Member cashout enabled" switch on
// the Payment Gateway card (backend MemberWalletService::isEnabled), which
// GET /settings/features also reports. With it off, Bank/Ewallet withdrawals
// are refused, so My Wallet is hidden. It is deliberately left out of
// FEATURES below so the Features card doesn't render it.
export const FEATURE_CASHOUT = 'cashout'

export const FEATURES = [
    {
        key: FEATURE_INCENTIVES,
        label: 'Incentives',
        description: 'The Incentives page and its menu entry.',
    },
    {
        key: FEATURE_ANNOUNCEMENTS,
        label: 'Announcements',
        description: 'The Announcements page, its menu entry, the header bell and the yellow announcement bar.',
    },
    {
        key: FEATURE_LOCATIONS,
        label: 'Locations',
        description: 'The admin Locations pages, the Location column on Members and the Location field on Add User.',
    },
    {
        key: FEATURE_VIP,
        label: 'VIP Members',
        description: 'VIP Access, the VIP role on Add User, the VIP columns and filter on Members, the VIP columns and field on Locations, and the VIP promo post on Announcements.',
    },
    {
        key: FEATURE_NEW_MEMBERS,
        label: 'New members',
        description: 'The "Welcome our newest members!" section on the dashboard.',
    },
    {
        key: FEATURE_FUNDS_NAV,
        label: 'Funds menu link',
        description: 'The Funds entry in the admin menu. The /admin/funds page itself stays open by URL.',
    },
    {
        key: FEATURE_PAYOUT_WITHDRAWALS,
        label: 'USDT payout withdrawals',
        description: 'Members can withdraw payouts to USDT. Off removes the USDT options and the server refuses USDT requests; Bank/Ewallet stays available (it follows "Member cashout enabled"). Requests already waiting can still be approved.',
    },
    {
        key: FEATURE_COMMISSION_WITHDRAWALS,
        label: 'USDT commission withdrawals',
        description: 'Members can withdraw commissions, VIP commissions included, to USDT. Off removes the USDT options and the server refuses USDT requests; Bank/Ewallet stays available. Requests already waiting can still be approved.',
    },
]

// Every feature starts enabled, so nothing disappears before the first fetch --
// except cashout, which is off by default on the backend too.
export const DEFAULT_FEATURES = {
    ...Object.fromEntries(FEATURES.map(f => [f.key, true])),
    [FEATURE_CASHOUT]: false,
}

// Nav items and routes (by their `key` in navigation.config / routes.config)
// that belong to a feature.
export const FEATURE_BY_KEY = {
    'userMenu.incentives': FEATURE_INCENTIVES,
    'userMenu.announcements': FEATURE_ANNOUNCEMENTS,
    announcements: FEATURE_ANNOUNCEMENTS,
    'adminMenu.locations': FEATURE_LOCATIONS,
    'adminMenu.locationNew': FEATURE_LOCATIONS,
    'adminMenu.locationEdit': FEATURE_LOCATIONS,
    vipAccess: FEATURE_VIP,
    'userMenu.wallet': FEATURE_CASHOUT,
}

// Nav items hidden by a feature while their route stays reachable by URL.
// /admin/funds must keep working: the payment gateway's paid-return
// redirect lands there.
export const NAV_ONLY_FEATURE_BY_KEY = {
    'adminMenu.funds': FEATURE_FUNDS_NAV,
}

export const isKeyEnabled = (key, features) => {
    const feature = FEATURE_BY_KEY[key]
    return !feature || features[feature] !== false
}

export const isNavKeyEnabled = (key, features) => {
    const feature = NAV_ONLY_FEATURE_BY_KEY[key]
    return isKeyEnabled(key, features) && (!feature || features[feature] !== false)
}
